'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface Feature {
  icon: string;
  title: string;
  description: string;
  benefits: string[];
  gradient: string;
}

interface FeaturesSectionProps {
  className?: string;
}

const FeaturesSection = ({ className = '' }: FeaturesSectionProps) => {
  const features: Feature[] = [
    {
      icon: 'SparklesIcon',
      title: 'Coach de IA Personalizado',
      description: 'Inteligencia artificial entrenada específicamente para emprendedores latinoamericanos, que comprende tu contexto y desafíos únicos.',
      benefits: [
        'Análisis de mercado regional',
        'Recomendaciones culturalmente relevantes',
        'Disponible 24/7 en español',
      ],
      gradient: 'from-blue-500 to-indigo-600',
    },
    {
      icon: 'ChartBarIcon',
      title: 'Análisis de Competencia',
      description: 'Identifica a tus competidores clave, analiza sus fortalezas y debilidades, y descubre oportunidades de diferenciación en tu mercado.',
      benefits: [
        'Identificación automática de competidores',
        'Análisis FODA detallado',
        'Estrategias de diferenciación',
      ],
      gradient: 'from-purple-500 to-pink-600',
    },
    {
      icon: 'DocumentTextIcon',
      title: 'Diagnósticos Empresariales',
      description: 'Evaluación completa de tu negocio con análisis en múltiples dimensiones: operaciones, finanzas, marketing, ventas y más.',
      benefits: [
        'Evaluación en 10+ dimensiones',
        'Identificación de áreas críticas',
        'Informes descargables',
      ],
      gradient: 'from-pink-500 to-rose-600',
    },
    {
      icon: 'LightBulbIcon',
      title: 'Planes de Acción Estratégicos',
      description: 'Recibe roadmaps personalizados con acciones concretas, priorizadas por impacto y factibilidad, para alcanzar tus objetivos.',
      benefits: [
        'Acciones priorizadas por ROI',
        'Cronogramas realistas',
        'Métricas de seguimiento',
      ],
      gradient: 'from-orange-500 to-amber-600',
    },
    {
      icon: 'ClockIcon',
      title: 'Resultados Instantáneos',
      description: 'No más esperar semanas por consultoría. Obtén análisis profesionales y recomendaciones en minutos, no en días.',
      benefits: [
        'Respuestas en tiempo real',
        'Sin agendar citas',
        'Acceso inmediato',
      ],
      gradient: 'from-green-500 to-emerald-600',
    },
    {
      icon: 'BoltIcon',
      title: 'Sistema de Tokens Flexible',
      description: 'Modelo de pago justo y transparente. Solo pagas por lo que usas, con recargas semanales automáticas cuando realmente las necesitas.',
      benefits: [
        'Plan gratis: 40 tokens iniciales + 10/semana si te quedan ≤5',
        'Plan LynMet: 100/mes + 20/semana si te quedan ≤5',
        'Sin contratos largos',
        'Precios transparentes',
      ],
      gradient: 'from-yellow-500 to-orange-500',
    },
  ];

  return (
    <section id="features" className={`hidden bg-background py-20 lg:block lg:py-32 ${className}`}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="section-eyebrow mb-6">
            <Icon name="StarIcon" size={20} className="text-primary" />
            <span>Funcionalidades Principales</span>
          </div>

          <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-6">
            Todo lo que Necesitas para{' '}
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'var(--gradient-app)' }}>
              Hacer Crecer
            </span>{' '}
            tu Negocio
          </h2>

          <p className="text-xl text-gray-600 leading-relaxed">
            Herramientas profesionales de coaching empresarial potenciadas por inteligencia artificial, diseñadas específicamente para emprendedores latinoamericanos.
          </p>
        </div>

        <div className="mb-12 rounded-3xl border border-[rgba(44,120,251,0.12)] bg-white/80 p-6 shadow-lg">
          <div className="grid gap-6 md:grid-cols-3">
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Para validar una idea</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Usa Gen-IA, SocrIAtes y AI-de para explorar oportunidades, hacer preguntas difíciles y bajar una idea a una propuesta más sólida.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Para ordenar un negocio</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Usa AI Coach para obtener un diagnóstico de negocio, definir prioridades y convertir problemas difusos en decisiones accionables.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Para crecer con foco</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Combina mentores, planes de acción y análisis de competencia para avanzar con más criterio sin depender de consultorías largas.
              </p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="app-card group rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-elevation-xl"
            >
              <div className={`w-16 h-16 bg-gradient-to-br ${feature.gradient} rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                <Icon name={feature.icon as any} size={32} className="text-white" />
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                {feature.title}
              </h3>

              <p className="text-gray-600 mb-6 leading-relaxed">{feature.description}</p>

              <ul className="space-y-3">
                {feature.benefits.map((benefit, benefitIndex) => (
                  <li key={benefitIndex} className="flex items-start space-x-3">
                    <Icon
                      name="CheckCircleIcon"
                      size={20}
                      className="text-green-500 flex-shrink-0 mt-0.5"
                    />
                    <span className="text-sm text-gray-700 font-medium">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
